import { __decorate, __metadata } from "tslib";
import { property, query, state } from 'lit/decorators.js';
import { registerTranslation } from '@italia/i18n';
import { BaseLocalizedComponent } from '../base-component/base-component.js';
import { FormControlController } from './form-controller.js';
import it from './locales/it.js';
registerTranslation(it);
export class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);
//# sourceMappingURL=form-control.js.map